import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as WorkspacesIcon,
  People as UsersIcon,
  FolderOpen as ProjectsIcon,
  Settings as SettingsIcon,
  AccessTime as TimeIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

interface NavigationItem {
  label: string;
  path: string;
  icon: React.ReactElement;
}

const navigationItems: NavigationItem[] = [
  { label: 'Dashboard', path: '/', icon: <DashboardIcon /> },
  { label: 'Workspaces', path: '/workspaces', icon: <WorkspacesIcon /> },
  { label: 'Users', path: '/users', icon: <UsersIcon /> },
  { label: 'Projects', path: '/projects', icon: <ProjectsIcon /> },
  { label: 'Time Entries', path: '/time-entries', icon: <TimeIcon /> },
  { label: 'Analytics', path: '/analytics', icon: <AnalyticsIcon /> },
];

const settingsItems: NavigationItem[] = [
  { label: 'Settings', path: '/settings', icon: <SettingsIcon /> },
];

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path: string) => {
    navigate(path);
    // Close mobile drawer if needed
    if (window.innerWidth < 960) {
      onToggle();
    }
  };

  const renderNavigationItem = (item: NavigationItem) => {
    const isSelected = location.pathname === item.path;
    
    const listItem = (
      <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          selected={isSelected}
          onClick={() => handleNavigation(item.path)}
          sx={{
            minHeight: 48,
            justifyContent: collapsed ? 'center' : 'initial',
            px: 2.5,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: collapsed ? 'auto' : 3,
              justifyContent: 'center',
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            sx={{ opacity: collapsed ? 0 : 1 }}
          />
        </ListItemButton>
      </ListItem>
    );

    if (collapsed) {
      return (
        <Tooltip key={item.path} title={item.label} placement="right">
          {listItem}
        </Tooltip>
      );
    }

    return listItem;
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo section */}
      <Toolbar sx={{ px: collapsed ? 1 : 2 }}>
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <TimeIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                fontStyle: 'italic',
              }}
            >
              <Box component="span" sx={{ color: 'primary.main' }}>Time</Box>
              <Box component="span" sx={{ color: 'secondary.main' }}>Lynxx</Box>
            </Typography>
          </Box>
        )}
        {collapsed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <TimeIcon sx={{ color: 'primary.main' }} />
          </Box>
        )}
      </Toolbar>

      <Divider />

      {/* Navigation items */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List sx={{ pt: 1 }}>
          {navigationItems.map(renderNavigationItem)}
        </List>
      </Box>

      {/* Settings section */}
      <Box>
        <Divider />
        <List>
          {settingsItems.map(renderNavigationItem)}
        </List>
      </Box>
    </Box>
  );
};